// Shared email-based identity matching for merging external systems
// (Vantagepoint/Deltek, OpenAsset) onto the Azure AD roster.
//
// The company merged several firms into one tenant (huckabee-inc.com,
// e4harchitecture.com, tska.com, rosstarrant.com, iegengineers.com, …) under
// moregroup-inc.com. A person's email domain often differs between the source
// system and Azure AD, so a full-email join misses. The local part (first.last)
// is stable across domains, and is collision-free across the active roster
// (verified: 662 active employees, 662 distinct local parts).

export function emailLocalPart(email) {
  const s = (email ?? '').toLowerCase().trim();
  const at = s.indexOf('@');
  return at === -1 ? s : s.slice(0, at);
}

// Build a lookup keyed by BOTH full email and local part. Full-email keys are
// added last so an exact address always resolves to its own record; on a
// local-part collision the first record seen wins. Records must have `.email`.
export function buildEmailMap(records) {
  const map = new Map();
  for (const r of records) {
    const lp = emailLocalPart(r.email);
    if (lp && !map.has(lp)) map.set(lp, r);
  }
  for (const r of records) {
    const full = (r.email ?? '').toLowerCase().trim();
    if (full) map.set(full, r);
  }
  return map;
}

// Resolve an Azure user's mail to a record: exact email first, then local-part
// fallback so the cross-domain case still joins.
export function matchByEmail(map, azureMail) {
  const full = (azureMail ?? '').toLowerCase().trim();
  if (!full) return undefined;
  return map.get(full) ?? map.get(emailLocalPart(full));
}
